import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import type { Vehicle } from '../../types/vehicle';
import BuildPickerCombobox, { type BuildPickerOption } from '../vehicles/BuildPickerCombobox';
import { optionFromGarageVehicle } from '../vehicles/buildPicker';

export type OwnerVehicleOption = BuildPickerOption;

interface OwnerVehicleComboboxProps {
  selected: OwnerVehicleOption | null;
  onSelect: (vehicle: OwnerVehicleOption | null) => void;
  /** Vehicle IDs already linked to this club (hidden from results). */
  excludeIds?: number[];
  disabled?: boolean;
  inputClassName?: string;
}

/**
 * Search/select from the signed-in user's garage — used where a raw vehicle ID is unusable.
 */
const OwnerVehicleCombobox: React.FC<OwnerVehicleComboboxProps> = ({
  selected,
  onSelect,
  excludeIds = [],
  disabled = false,
  inputClassName = 'input flex-1',
}) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getUserVehicles()
      .then((list) => {
        if (cancelled) return;
        setVehicles(Array.isArray(list) ? list : []);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setVehicles([]);
        setLoadError(e instanceof Error ? e.message : 'Could not load your garage');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => vehicles.map(optionFromGarageVehicle), [vehicles]);
  const emptyMessage =
    vehicles.length === 0 ? 'No builds in your garage yet' : 'All of your garage builds are already linked';

  return (
    <BuildPickerCombobox
      label="Search your garage"
      placeholder={loading ? 'Loading your builds…' : 'Type year, make, model, or nickname…'}
      options={options}
      loading={loading}
      error={loadError}
      emptyMessage={emptyMessage}
      noMatchMessage="No matching builds"
      selected={selected ? [selected] : []}
      onChange={(next) => onSelect(next[0] || null)}
      excludeIds={excludeIds}
      disabled={disabled}
      inputClassName={inputClassName}
      testId="owner-vehicle-search"
    />
  );
};

export default OwnerVehicleCombobox;
